'use client';

import { Bar } from '@/components/charts';
import { SelectionTable } from '@/components/form';
import { useAppSelector } from '@/state';
import { ProcurementApi } from '@/state/api';
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Separator,
  Text,
} from '@chakra-ui/react';
import { Card, TableColumnsConfigType } from '@ui';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useMemo } from 'react';
import { LuDot, LuTriangleAlert } from 'react-icons/lu';

dayjs.extend(relativeTime);

// -----------------------------
// types (match dashboard API payload)
// -----------------------------
type DashboardKpis = {
  on_schedule_rate: number; // 0..1
  delayed_lines: number;
  at_risk_lines: number;
  total_open_lines: number;
  ready_rate: number; // 0..1
};

type OpenLinesBySupplierRow = {
  supplier_id: number | null;
  supplier_name: string;
  open_lines: number;
  overdue_lines: number;
  due_soon_lines: number;
};

type MaterialsAtRiskRow = {
  po_id: number;
  project_po_number: string | null;
  po_number: string;
  title: string | null;
  supplier_id: number | null;
  supplier_name: string;
  part_id: number | null;
  part_number: string;
  description: string | null;
  qty_ordered: number;
  qty_received: number;
  due_at: string | null;
  risk: 'OVERDUE' | 'DUE_SOON';
  days_delta: number | null; // overdue days (+) | days until due (0/-)
};

type SupplierRiskRow = {
  supplier_id: number | null;
  supplier_name: string;
  open_lines: number;
  overdue_lines: number;
  overdue_rate: number; // 0..1
};

type ProcurementDashboardResponse = {
  project_id: number;
  updated_at: string;
  window_days: number;
  kpis: DashboardKpis;
  open_lines_by_supplier: OpenLinesBySupplierRow[];
  materials_at_risk: MaterialsAtRiskRow[];
  supplier_risk: SupplierRiskRow[];
};

// -----------------------------
// helpers
// -----------------------------
const pct = (v?: number | null) => `${Math.round(((v ?? 0) as number) * 100)}%`;

const fmtDate = (v?: string | null) =>
  v ? dayjs(v).format('YYYY-MM-DD') : '—';

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

const kpiTone = (
  value: number,
  { good, warn }: { good: number; warn: number },
) => {
  // return palette + label
  if (value >= good) return { palette: 'green', label: 'GOOD' };
  if (value >= warn) return { palette: 'yellow', label: 'WATCH' };
  return { palette: 'red', label: 'RISK' };
};

const Dashboard = () => {
  const selectedProject = useAppSelector(
    (state) => state.project.selectedProject,
  );
  const projectId = selectedProject?.id as number | undefined;

  // control risk horizon (due soon)
  const windowDays = 7;

  const { data, isFetching, isLoading, refetch } =
    ProcurementApi.Api.useGetProcurementDashboardQuery(
      { query: { project: projectId as number, window_days: windowDays } },
      { skip: !projectId },
    );

  const payload = data as ProcurementDashboardResponse | undefined;
  const kpis = payload?.kpis;

  const totalOpen = kpis?.total_open_lines ?? 0;
  const delayed = kpis?.delayed_lines ?? 0;
  const atRisk = kpis?.at_risk_lines ?? 0;

  const onScheduleRate = kpis?.on_schedule_rate ?? 0;
  const readyRate = kpis?.ready_rate ?? 0;

  const delayedPct = useMemo(() => {
    if (!totalOpen) return '0%';
    return `${Math.round((delayed / totalOpen) * 100)}%`;
  }, [delayed, totalOpen]);

  const updatedLabel = useMemo(() => {
    if (!payload?.updated_at) return '—';
    const d = dayjs(payload.updated_at);
    return d.isValid() ? d.fromNow() : '—';
  }, [payload?.updated_at]);

  // -----------------------------
  // data mapping (consistent + compact)
  // -----------------------------
  const suppliersOpenChart = useMemo(() => {
    return (payload?.open_lines_by_supplier ?? []).map((r) => ({
      label: r.supplier_name,
      open: r.open_lines,
      overdue: r.overdue_lines,
      due_soon: r.due_soon_lines,
      at_risk: r.overdue_lines + r.due_soon_lines,
      ready: Math.max(0, r.open_lines - r.overdue_lines),
    }));
  }, [payload?.open_lines_by_supplier]);

  const riskTableItems = useMemo(() => {
    return (payload?.materials_at_risk ?? []).map((r, idx) => ({
      id: idx + 1,
      part_number: r.part_number,
      description: r.description ?? '—',
      supplier: r.supplier_name,
      project_po_number: r.project_po_number ?? r.po_number,
      po_number: r.po_number,
      po_title: r.title ?? '—',
      due_at: r.due_at,
      risk: r.risk,
      days_delta: r.days_delta,
      qty: `${r.qty_received}/${r.qty_ordered}`,
    }));
  }, [payload?.materials_at_risk]);

  const supplierRiskItems = useMemo(() => {
    return (payload?.supplier_risk ?? []).map((r, idx) => ({
      id: idx + 1,
      supplier: r.supplier_name,
      overdue_rate: r.overdue_rate,
      overdue_lines: r.overdue_lines,
      open_lines: r.open_lines,
    }));
  }, [payload?.supplier_risk]);

  // Alerts = top overdue rows (most overdue first)
  const alerts = useMemo(() => {
    const rows = (payload?.materials_at_risk ?? [])
      .filter((r) => r.risk === 'OVERDUE')
      .sort((a, b) => (b.days_delta ?? 0) - (a.days_delta ?? 0))
      .slice(0, 3);

    return rows.map((a) => ({
      key: `${a.po_id}-${a.part_number}`,
      title: `${a.part_number} delayed`,
      supplier: a.supplier_name,
      po: a.project_po_number ?? a.po_number,
      poReference: a.po_number,
      due: a.due_at,
      overdueDays: a.days_delta ?? null,
    }));
  }, [payload?.materials_at_risk]);

  // -----------------------------
  // UI: empty/loading
  // -----------------------------
  if (!projectId) {
    return (
      <Card>
        <Text>Select a project to view Purchase Orders Dashboard.</Text>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <Text>Loading…</Text>
      </Card>
    );
  }

  // KPI tones (simple, readable)
  const onScheduleTone = kpiTone(onScheduleRate, { good: 0.9, warn: 0.75 });
  const delayedTone = kpiTone(totalOpen ? 1 - delayed / totalOpen : 1, {
    good: 0.9,
    warn: 0.8,
  }); // higher is better (less delayed)
  const atRiskTone = kpiTone(totalOpen ? 1 - atRisk / totalOpen : 1, {
    good: 0.9,
    warn: 0.8,
  }); // higher is better (less at risk)
  const readinessTone = kpiTone(readyRate, { good: 0.9, warn: 0.8 });

  return (
    <Flex flexDirection="column" gapY={4}>
      {/* =========================
          Header (one clean place)
          ========================= */}
      <Card>
        <Flex
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={3}
        >
          <Flex flexDirection="column" gap={1} minW="280px">
            <Text fontWeight="bold" fontSize="lg">
              Purchase Orders Dashboard
            </Text>
            <Flex
              alignItems="center"
              flexWrap="wrap"
              fontSize="xs"
              color="fg.subtle"
              gap={2}
            >
              <Text>
                {String(
                  (selectedProject as any)?.project_code ??
                    (selectedProject as any)?.job_number ??
                    '',
                ) || 'Project'}
              </Text>
              <LuDot />
              <Text>Risk window: {windowDays} days</Text>
              <LuDot />
              <Text>
                {isFetching ? 'Refreshing…' : `Updated ${updatedLabel}`}
              </Text>
            </Flex>
          </Flex>

          <Flex alignItems="center" gap={2} flexWrap="wrap">
            <Badge bg="border.emphasized" color="fg">
              <Text>Open lines: {totalOpen}</Text>
            </Badge>
            <Button
              size="sm"
              variant="outline"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              Refresh
            </Button>
          </Flex>
        </Flex>
      </Card>

      {/* =========================
          KPI Row (single row, super interpretable)
          ========================= */}
      <Grid templateColumns="repeat(auto-fit, minmax(240px, 1fr))" gap={4}>
        <KpiCard
          title="On-time material"
          value={pct(onScheduleRate)}
          helper="Open lines with due dates that are not overdue"
          badge={onScheduleTone.label}
          badgePalette={onScheduleTone.palette}
        />
        <KpiCard
          title="Delayed lines"
          value={`${delayed}`}
          subValue={`(${delayedPct} of open)`}
          helper="Open lines past due date"
          badge={delayedTone.label}
          badgePalette={delayedTone.palette}
        />
        <KpiCard
          title="At risk"
          value={`${atRisk}`}
          helper={`Overdue + due soon (next ${windowDays}d)`}
          badge={atRiskTone.label}
          badgePalette={atRiskTone.palette}
        />
        <KpiCard
          title="Readiness"
          value={pct(readyRate)}
          helper="Open lines not overdue (proxy for install readiness)"
          badge={readinessTone.label}
          badgePalette={readinessTone.palette}
        />
      </Grid>

      {/* =========================
          Main content: 2-column layout
          Left = actionable list
          Right = supplier focus + alerts
          ========================= */}
      <Grid
        templateColumns={{ base: '1fr', lg: '1.6fr 1fr' }}
        gap={4}
        alignItems="start"
      >
        {/* LEFT: materials at risk (primary) */}
        <Card>
          <Flex flexDirection="column" gapY={3}>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
            >
              <Flex flexDirection="column">
                <Text fontWeight="bold">Materials at Risk</Text>
                <Text fontSize="xs" color="fg.subtle">
                  The list you work from (calls, expediting, rescheduling)
                </Text>
              </Flex>
              <Badge bg="border.emphasized" color="fg">
                <Text>{riskTableItems.length} items</Text>
              </Badge>
            </Flex>

            <SelectionTable<any>
              columns={materialsAtRiskColumns}
              items={riskTableItems}
              height="440px"
              stickyColumns
            />
          </Flex>
        </Card>

        {/* RIGHT: alerts + supplier risk */}
        <Flex flexDirection="column" gapY={4}>
          {/* Alerts */}
          <Card>
            <Flex flexDirection="column" gapY={3}>
              <Flex justifyContent="space-between" alignItems="center">
                <Text fontWeight="bold">Alerts</Text>
                <Badge bg="border.emphasized" color="fg">
                  <Text>{alerts.length}</Text>
                </Badge>
              </Flex>

              {alerts.length ? (
                <Grid templateColumns="1fr" gap={3}>
                  {alerts.map((a) => (
                    <Box
                      key={a.key}
                      borderWidth="1px"
                      borderColor="border.emphasized"
                      rounded="md"
                      p={3}
                    >
                      <Flex
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                      >
                        <Badge colorPalette="red">
                          <Text>High</Text>
                        </Badge>
                        <LuTriangleAlert color="red" />
                      </Flex>

                      <Text fontWeight="semibold" fontSize="sm">
                        {a.title}
                      </Text>

                      <Flex
                        mt={2}
                        fontSize="xs"
                        color="fg.subtle"
                        gap={2}
                        flexWrap="wrap"
                      >
                        <Text>Supplier: {a.supplier}</Text>
                        <LuDot />
                        <Text>PO {a.po}</Text>
                        {a.poReference && a.poReference !== a.po && (
                          <>
                            <LuDot />
                            <Text>Reference {a.poReference}</Text>
                          </>
                        )}
                      </Flex>

                      <Text fontSize="xs" color="fg.subtle" mt={1}>
                        Due: {fmtDate(a.due)} •{' '}
                        {a.overdueDays != null
                          ? `Overdue ${a.overdueDays}d`
                          : '—'}
                      </Text>

                      <Text fontSize="xs" mt={2}>
                        <Text as="span" fontWeight="bold">
                          Next action:
                        </Text>{' '}
                        <Text as="span" color="fg.subtle">
                          Confirm promise date, escalate if needed
                        </Text>
                      </Text>
                    </Box>
                  ))}
                </Grid>
              ) : (
                <Text fontSize="sm" color="fg.subtle">
                  No high-risk alerts right now.
                </Text>
              )}
            </Flex>
          </Card>

          {/* Supplier risk */}
          <Card>
            <Flex flexDirection="column" gapY={3}>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <Flex flexDirection="column">
                  <Text fontWeight="bold">Supplier risk</Text>
                  <Text fontSize="xs" color="fg.subtle">
                    Reliability view (overdue % on open lines)
                  </Text>
                </Flex>
                <Badge bg="border.emphasized" color="fg">
                  <Text>Top {Math.min(10, supplierRiskItems.length)}</Text>
                </Badge>
              </Flex>

              <SelectionTable<any>
                columns={supplierRiskColumns}
                items={supplierRiskItems}
                height="320px"
              />
            </Flex>
          </Card>
        </Flex>
      </Grid>

      {/* =========================
          Bottom: one clear analytics section (not 4 disconnected charts)
          ========================= */}
      <Card>
        <Flex flexDirection="column" gapY={3}>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Flex flexDirection="column">
              <Text fontWeight="bold">Supplier workload & risk</Text>
              <Text fontSize="xs" color="fg.subtle">
                Open vs overdue vs due soon by supplier
              </Text>
            </Flex>
            <Badge bg="border.emphasized" color="fg">
              <Text>Suppliers: {suppliersOpenChart.length}</Text>
            </Badge>
          </Flex>

          <Separator />

          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
            <Card>
              <Text fontWeight="semibold" fontSize="sm" mb={2}>
                Open lines by supplier
              </Text>
              <Bar
                data={suppliersOpenChart.map((r) => ({
                  shipping_list_name: r.label,
                  total_parts: r.open,
                }))}
                height={260}
                generateColors
                defaultStackId="open-by-supplier"
                xAxis={{ dataKey: 'shipping_list_name' }}
                yAxis={{ dataKey: 'total_parts' }}
                tooltip={{
                  title: { label: 'Supplier', valueKey: 'shipping_list_name' },
                  items: [{ label: 'Open lines', valueKey: 'total_parts' }],
                }}
                legend={{ labelKey: 'shipping_list_name' }}
                series={[{ dataKey: 'total_parts', name: 'Open lines' }]}
              />
            </Card>

            <Card>
              <Text fontWeight="semibold" fontSize="sm" mb={2}>
                Overdue + Due soon by supplier
              </Text>
              <Bar
                data={suppliersOpenChart.map((r) => ({
                  shipping_list_name: r.label,
                  total_parts: r.at_risk,
                }))}
                height={260}
                generateColors
                defaultStackId="risk-by-supplier"
                xAxis={{ dataKey: 'shipping_list_name' }}
                yAxis={{ dataKey: 'total_parts' }}
                tooltip={{
                  title: { label: 'Supplier', valueKey: 'shipping_list_name' },
                  items: [{ label: 'At risk lines', valueKey: 'total_parts' }],
                }}
                legend={{ labelKey: 'shipping_list_name' }}
                series={[{ dataKey: 'total_parts', name: 'At risk lines' }]}
              />
              <Text fontSize="xs" color="fg.subtle" mt={2}>
                “At risk” = overdue + due in next {windowDays} days.
              </Text>
            </Card>
          </Grid>
        </Flex>
      </Card>
    </Flex>
  );
};

export default Dashboard;

// -----------------------------
// UI components
// -----------------------------
const KpiCard = ({
  title,
  value,
  subValue,
  helper,
  badge,
  badgePalette,
}: {
  title: string;
  value: string;
  subValue?: string;
  helper: string;
  badge: string;
  badgePalette: 'green' | 'yellow' | 'red';
}) => {
  return (
    <Card>
      <Flex flexDirection="column" gap={2}>
        <Flex justifyContent="space-between" alignItems="center">
          <Text fontWeight="bold" fontSize="sm">
            {title}
          </Text>
          <Badge colorPalette={badgePalette as any}>
            <Text>{badge}</Text>
          </Badge>
        </Flex>

        <Flex alignItems="baseline" gap={2}>
          <Text fontSize="2xl" fontWeight="bold">
            {value}
          </Text>
          {subValue ? (
            <Text fontSize="xs" color="fg.subtle">
              {subValue}
            </Text>
          ) : null}
        </Flex>

        <Text fontSize="xs" color="fg.subtle">
          {helper}
        </Text>
      </Flex>
    </Card>
  );
};

// -----------------------------
// tables
// -----------------------------
const materialsAtRiskColumns: TableColumnsConfigType<any> = [
  { id: { label: 'Id', width: '70px' } },
  { part_number: { label: 'Part', width: '170px' } },
  { description: { label: 'Description', width: '240px' } },
  {
    risk: {
      label: 'Risk',
      width: '120px',
      render: (row) => (
        <Badge colorPalette={row.risk === 'OVERDUE' ? 'red' : 'yellow'}>
          <Text>{row.risk}</Text>
        </Badge>
      ),
    },
  },
  { supplier: { label: 'Supplier', width: '180px' } },
  {
    due_at: {
      label: 'Due',
      width: '140px',
      render: (row) => <Text>{fmtDate(row.due_at)}</Text>,
    },
  },
  {
    days_delta: {
      label: 'Days ±',
      width: '110px',
      render: (row) => (
        <Text>
          {row.days_delta == null
            ? '—'
            : row.days_delta > 0
              ? `+${row.days_delta}`
              : `${row.days_delta}`}
        </Text>
      ),
    },
  },
  { qty: { label: 'Recv/Ord', width: '120px' } },
  {
    project_po_number: { label: 'PO Number', width: '180px' },
  },
  { po_title: { label: 'PO Title', width: '180px' } },
];

const supplierRiskColumns: TableColumnsConfigType<any> = [
  { id: { label: '#', width: '60px' } },
  { supplier: { label: 'Supplier', width: '220px' } },
  {
    overdue_rate: {
      label: 'Overdue %',
      width: '120px',
      render: (row) => <Text>{pct(row.overdue_rate)}</Text>,
    },
  },
  { overdue_lines: { label: 'Overdue', width: '100px' } },
  { open_lines: { label: 'Open', width: '100px' } },
];
